import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES, Text } from '@contentful/rich-text-types';
import { MergeTag } from '@ninetailed/experience.js-next';
import CollapsibleText from 'components/collapsible-text';
import BlogImage from '../sections/blog-image';
import BlogSectionCta from '../sections/blog-section-cta';
import Image from '../sections/components/image';
import CustomerIoLeadForm from '../sections/customer-io-lead-form';
import FaqSection from '../sections/faq-section';
import ProductSlider from '../sections/product-slider';
import Video from '../sections/video';
import { processCollapsibleText } from './process-collapsible-text';
import style from './util.module.css';

/**
 * Render a rich text document from Contentful.
 * The document can contain various types of content, like text, images, videos, etc.
 *  If a text node contains a newline character, it will be rendered as a line break.
 * @see https://github.com/contentful/rich-text/tree/8b31b855f758fd8f26a041270903eadd6ebe337f/packages/rich-text-react-renderer
 */
const renderDocument = (richTextDocument: Document): React.ReactNode => {
  // Process the document to handle CollapsibleText blocks spanning multiple paragraphs
  const processedDocument = processCollapsibleText(richTextDocument);

  const renderOptions: Options = {
    renderText: (text) => {
      // Handle normal text with line breaks
      return text
        .split('\n')
        .reduce<React.ReactNode[]>((children, textSegment, index) => {
          return [...children, index > 0 && <br key={index} />, textSegment];
        }, []);
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => {
        // Skip paragraphs marked for skipping
        if (node.data?.skipRendering) return null;

        // If this is a processed CollapsibleText paragraph
        if (node.data?.isCollapsibleText) {
          const content = node.data.collapsibleContent;

          // Process the content to replace \\n\\n with actual newlines
          const processedContent = content.replace(/\\n\\n/g, '\n\n');

          return (
            <CollapsibleText
              text={processedContent}
              previewWordCount={node.data.previewWordCount}
            />
          );
        }

        // Handle regular paragraphs
        if (
          node.content.length === 1 &&
          node.content[0].nodeType === 'text' &&
          (node.content[0] as Text).value.includes('\n')
        ) {
          return <>{children}</>;
        }
        return <p>{children}</p>;
      },
      [INLINES.EMBEDDED_ENTRY]: (node) => {
        if (node.data.target.sys.contentType.sys.id === 'nt_mergetag') {
          return <MergeTag id={node.data.target.fields.id} />;
        } else if (node.data.target.sys.contentType.sys.id === 'sectionFaQs') {
          return <FaqSection {...node.data.target.fields} />;
        }
      },
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        return <Image {...node.data.target} />;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        if (
          node.data.target.sys.contentType.sys.id === 'sectionProductSlider'
        ) {
          return <ProductSlider {...node.data.target.fields} isBlogPost />;
        } else if (node.data.target.sys.contentType.sys.id === 'sectionFaQs') {
          return (
            <div className="pb-10 pt-10">
              <FaqSection {...node.data.target.fields} />
            </div>
          );
        } else if (node.data.target.sys.contentType.sys.id === 'sectionVideo') {
          return <Video {...node.data.target.fields} disableSpacing />;
        } else if (
          node.data.target.sys.contentType.sys.id === 'blogArticleImage'
        ) {
          return <BlogImage {...node.data.target.fields} />;
        } else if (
          node.data.target.sys.contentType.sys.id === 'blogSectionCta'
        ) {
          return <BlogSectionCta {...node.data.target.fields} />;
        } else if (
          node.data.target.sys.contentType.sys.id ===
          'sectionCustomerIoLeadForm'
        ) {
          return <CustomerIoLeadForm {...node.data.target.fields} />;
        }
      },
      [BLOCKS.HEADING_1]: (node, children) => <h1>{children}</h1>,
      [BLOCKS.HEADING_2]: (node, children) => {
        const props = {};
        if (node.content[0].nodeType === 'text')
          props['id'] = (node.content[0] as Text).value;

        return <h2 {...props}>{children}</h2>;
      },
    },
  };

  return (
    <>
      <div className={style.normalize} data-test="rich-text-content">
        {documentToReactComponents(processedDocument, renderOptions)}
      </div>
    </>
  );
};

export default renderDocument;
